import React, { useEffect, useState } from "react";
import { Card, Col, Grid, Menu, Row, Tabs, Typography } from "antd";
import {
  MailOutlined,
  DollarOutlined,
  LockOutlined,
  TagsOutlined,
  UsergroupAddOutlined,
  WhatsAppOutlined,
  RobotOutlined,
  SettingOutlined,
  EditOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import ChangePassword from "./Sections/ChangePassword";
import ManageTopicsComponent from "./Sections/ManageTopicsComponent";
import ManageAgentsComponent from "./Sections/ManageAgentsComponent";
import WhatsappSetting from "./Sections/WhatsappSetting";
import { useSearchParams, useLocation } from "react-router-dom";
import EmailSettings from "./Sections/EmailSettings";
import BillingUsagePage from "./Sections/BillingUsageComponent";
import AiSettingsComponents from "./Sections/AISettingsComponent";
import AISetupComponent from "./Sections/AISetupComponent";
import { getAllSettingsApi } from "../../services/settings.service";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { transformStringForDash } from "../../helpers/channelNameTransformation";
import SocialMediaProfileSettingsComponent from "./Sections/SocialMediaProfileSettings";
import MessageTemplateSettings from "./Sections/MessageTemplate";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const SettingsComponent = ({ setLoading }) => {
  const dispatch = useAppDispatch();
  const role = localStorage.getItem("role");
  const [searchParams, setSearchParams] = useSearchParams();
  const [settingFirstItem, setSettingFirstItem] = useState();
  const tabParam = searchParams.get("tab");
  const activeTab = tabParam ? tabParam.split('?')[0] : settingFirstItem;

  const [menuItems, setMenuItems] = useState([]);
  const location = useLocation();
  const screens = useBreakpoint();
  const isCompactLayout = !screens.lg;

  // Fix malformed URLs with multiple ? query params
  useEffect(() => {
    const search = location.search;
    // Check if there are multiple ? in the URL
    if (search.split('?').length > 2) {
      // Split by ? and get all parts after the first ?
      const parts = search.substring(1).split('?');
      // Join them with & instead
      const fixedSearch = parts.join('&');
      // Navigate to the corrected URL
      setSearchParams(new URLSearchParams(fixedSearch), { replace: true });
    }
  }, [location.search, setSearchParams]);

  const handleMenuClick = ({ key }) => {
    setSearchParams({ tab: key });
  };

  const handleTabChange = (key) => {
    setSearchParams({ tab: key });
  };

  const renderContent = () => {
    const isTabInMenu = menuItems.some((item) => item.key === activeTab);
    if (!isTabInMenu) return null;

    switch (activeTab) {
      case "ai-settings":
        return <AISetupComponent />;
      case "change-password":
        return <ChangePassword />;
      case "manage-topics":
        return <ManageTopicsComponent />;
      case "manage-users":
        return <ManageAgentsComponent />;
      case "whatsapp-settings":
        return <WhatsappSetting />;
      case "billing-usage":
        return <BillingUsagePage setLoading={setLoading} />;
      case "email-settings":
        return <EmailSettings />;
      case "social-media-profile-settings":
        return <SocialMediaProfileSettingsComponent />;
      case "message-templates":
        return <MessageTemplateSettings />;
      default:
        return null;
    }
  };

  const fetchAllSettings = () => {
    setLoading(true);
    dispatch(
      getAllSettingsApi({
        params: {
          filter_route: "settings",
        },
        successCallback: (response) => {
          const menuItems = response?.setting_menu || [];

          const menuItemsList = menuItems?.map((item) => {
            const mappedItem = item === "manage-agents" ? "manage-users" : item;
            return {
              key: mappedItem,
              icon:
                item === "ai-settings" ? (
                  <RobotOutlined />
                ) : item === "change-password" ? (
                  <LockOutlined />
                ) : item === "manage-topics" ? (
                  <TagsOutlined />
                ) : item === "manage-agents" ? (
                  <UsergroupAddOutlined />
                ) : item === "whatsapp-settings" ? (
                  <WhatsAppOutlined />
                ) : item === "billing-usage" ? (
                  <DollarOutlined />
                ) : item === "email-settings" ? (
                  <MailOutlined />
                ) : item === "social-media-profile-settings" ? (
                  <GlobalOutlined />
                ) : item === "message-templates" ? (
                  <EditOutlined />
                ) : (
                  <SettingOutlined />
                ),
              label:
                item === "manage-agents"
                  ? "Manage Users"
                  : item === "billing-usage"
                  ? "Billing & Usage"
                  : transformStringForDash(item),
            };
          });

          setSettingFirstItem(menuItemsList[0]?.key);
          setMenuItems(menuItemsList);
        },
        failureCallback: (error) => {},
        finalCallback: () => {
          setLoading(false);
        },
      })
    );
  };

  useEffect(() => {
    fetchAllSettings();
  }, []);

  // Scroll to hash when location or activeTab changes
  useEffect(() => {
    if (!location || !location.hash) return;

    // Only scroll when the activeTab is ai-settings
    if (activeTab !== "ai-settings") return;

    // Small delay to ensure elements have rendered
    const id = location.hash.replace("#", "");
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Also adjust for any fixed headers (optional offset)
        const yOffset = -80; // adjust if header height differs
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [location, activeTab]);

  return (
    <div>
      <style>
        {`
          .settings-menu .ant-menu-item-selected {
            background-color: #e6f7ff !important;
            color: #1d39c4 !important;
          }
          .settings-menu .ant-menu-item-selected .anticon {
            color: #1d39c4 !important;
          }
          .settings-menu .ant-menu-item:hover {
            background-color: #d6eaff !important;
            color: #1d39c4 !important;
          }
          .settings-menu .ant-menu-item-selected:hover {
            background-color: #e6f7ff !important;
            color: #1d39c4 !important;
          }
          .settings-menu .ant-menu-item-selected:hover .anticon {
            color: #1d39c4 !important;
          }
          .sidebar-sticky {
            position: sticky;
            top: 0px;
            max-height: calc(100vh - 32px);
            overflow-y: auto;
          }
          .settings-container {
            min-height: calc(100vh - 32px);
            padding-bottom: 50px;
          }
          .settings-tabs-card .ant-tabs-nav {
            margin: 0 0 8px 0;
          }
          .settings-tabs-card .ant-tabs-tab {
            padding: 8px 12px;
          }
          .settings-tab-label {
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }
        `}
      </style>
      <Row gutter={24} className="settings-container">
        <Col xs={0} sm={0} md={0} lg={6}>
          <div className="sidebar-sticky">
            <Card>
              <Title
                level={4}
                style={{
                  marginBottom: "8px",
                  paddingBottom: "8px",
                  borderBottom: "1px solid #e6f7ff",
                }}
              >
                Settings
              </Title>
              <Menu
                className="settings-menu"
                mode="vertical"
                selectedKeys={[activeTab]}
                onClick={handleMenuClick}
                items={menuItems}
                style={{ border: "none" }}
              />
            </Card>
          </div>
        </Col>
        <Col xs={24} sm={24} md={24} lg={18}>
          {isCompactLayout && (
            <Card className="settings-tabs-card" style={{ marginBottom: 16 }}>
              <Title
                level={4}
                style={{
                  marginBottom: "8px",
                  paddingBottom: "8px",
                  borderBottom: "1px solid #e6f7ff",
                }}
              >
                Settings
              </Title>
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={menuItems.map((item) => ({
                  key: item.key,
                  label: (
                    <span className="settings-tab-label">
                      {item.icon}
                      <span>{item.label}</span>
                    </span>
                  ),
                }))}
              />
            </Card>
          )}
          {renderContent()}
        </Col>
      </Row>
    </div>
  );
};

export default SettingsComponent;